import { getAuthToken } from 'lib/auth'
import NProgress from 'nprogress'
import redirectTo from 'utils/redirectTo'
import { OperationName, QueryResponse } from '../types'
import { handleErrors } from 'utils/error-handling'
import { GraphQLError } from 'utils/error-handling/graphql-error'
import { store } from 'App'
NProgress.configure({ showSpinner: false })

export const API_URL = process.env.REACT_APP_API_URL!

type JsonErrors = GraphQLError
export type JsonData<D = any> = { data: D extends OperationName ? QueryResponse<D> : D }

const getApiName = (query) => {
  const nameRegex = /\b(query|mutation)\s*(\w+)\s*\(/
  const match = query.match(nameRegex)
  return match ? match[2].trim() : null
}
class API {
  fetch = async <D = any, V = any>(query: string, variables: V, onError = handleErrors) => {
    NProgress.start()
    const authToken = await getAuthToken()
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: {
        ...(authToken && { Authorization: authToken }),
      },
      body: JSON.stringify({ query, variables }),
      keepalive: true,
      cache: 'no-cache',
    })
    if (res.status === 401) {
      if (window.location.href.includes('/auth/login')) return
      redirectTo('/auth/login', window.location.search)
    }
    const json = (await res.json()) as JsonData<D> & JsonErrors
    NProgress.done()
    if (json?.errors?.length) {
      const state = store.getState()
      const userId = state.user?.user?.id ?? ''
      onError(json.errors, {
        arguments: variables,
        apiName: getApiName(query),
        operation: json.errors?.[0].path?.[0],
        userId,
      })
    }
    return json?.data
  }
  uploadFile = async (
    url: string,
    formData: File | Blob,
    type: string,
    onProgress?: (percentage: number) => void,
    signal?: AbortSignal
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      // Support abort signal
      if (signal) {
        signal.addEventListener('abort', () => {
          xhr.abort()
          reject(new Error('Upload cancelled'))
        })
      }

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentage = Math.round((e.loaded / e.total) * 100)
          onProgress(percentage)
        }
      })

      xhr.addEventListener('load', () => {
        NProgress.done()
        if (xhr.status === 200) {
          resolve(xhr.responseURL.split('?')[0])
        } else {
          reject(new Error('Failed to upload'))
        }
      })

      xhr.addEventListener('error', () => {
        NProgress.done()
        reject(new Error('Failed to upload'))
      })

      xhr.addEventListener('abort', () => {
        NProgress.done()
        reject(new Error('Upload cancelled'))
      })

      xhr.open('PUT', url)
      xhr.setRequestHeader('Content-Type', type || 'multipart/form-data')
      NProgress.start()
      xhr.send(formData)
    })
  }
}

// eslint-disable-next-line
export default new API()
